import { CDN_URL } from "@supplier/constants";
import { OSS_URL_SUPPLIER } from "@/config";

export type ThemeOptions = {
    /**
     * 系统名称
     */
    systemName: string;
    /**
     * 系统名称图片
     */
    systemSvg: string;
    /**
     * 系统简称
     */
    systemAbbreviation: string;
    /**
     * 系统英语名称
     */
    systemEnglishName: string;
    /**
     * 系统头部标语
     */
    systemHeaderSlogan: string;
    /**
     * 版权
     */
    copyright: string;
    /**
     * 备案
     */
    icpFilings: string;
    /**
     * 备案网址
     */
    icpFilingsUrl: string;
    /**
     * 联网备案信息
     */
    internetFilings: string;
    /**
     * 联网备案信息网址
     */
    internetFilingsUrl: string;
    /**
     * 创造者
     */
    poweredBy: string;
    /**
     * 主题颜色
     */
    themeColor: string;
    /**
     * 登录页logo
     */
    loginLogo: string;
    /**
     * 登录页背景图
     */
    loginBgColor: string;
    /**
     * 公众页头部logo
     */
    publicHeaderLogo: string;
    /**
     * 公众页横幅
     */
    publicBanner: string;
    /**
     * 公众页底部logo
     */
    publicFooterLogo: string;
    /**
     * 账号头像
     */
    accountAvatar: string;
    buttonTextColor: string;
};

export type ITheme = {
    /**
     * 主题色列表
     */
    themeColors: {
        light: string[];
        dark: string[];
    };
    /**
     * 按钮文本颜色
     * */
    buttonTextColor: string;
} & ThemeOptions;

const ENV_TITLE = import.meta.env.VITE_GLOB_APP_TITLE as string;
const ENV_TITLE_SVG = import.meta.env.VITE_GLOB_APP_TITLE_SVG as string;

const ENV_TITLE_EN = import.meta.env.VITE_GLOB_APP_TITLE_EN as string;
const ENV_HEADER_LOGO = import.meta.env.VITE_SUPPLIER_HEADER_LOGO as string;

const isProdEnv = import.meta.env.MODE === "prod";
// 鑫链运控
const copyright = `Copyright © 2024-${new Date().getFullYear()} 志豪链云科技有限公司 All Rights Reserved.`;
const icpFilings = `鄂B2-20240693`;
const internetFilings = `鄂公网安备42110002000194号`;

// 生产环境 网站名称调整为【鑫东物流】
const copyRightXinDongWuLiu = `Copyright © 2025 江西建业供应链管理有限公司`;
const icpFilingsXinDongWuLiu = "";
const internetFilingsXinDongWuLiu = `赣ICP备2024042736号`;
// 生产环境 补充备案信息
const poweredBy = `Powered by 志豪链云科技有限公司`;

export const defaultTheme: ThemeOptions = {
    systemName: ENV_TITLE,
    systemSvg: ENV_TITLE_SVG,
    systemAbbreviation: ENV_TITLE,
    systemEnglishName: ENV_TITLE_EN,
    systemHeaderSlogan: "产业新质生产力服务商",
    copyright: isProdEnv ? copyRightXinDongWuLiu : copyright,
    icpFilings: isProdEnv ? icpFilingsXinDongWuLiu : icpFilings,
    icpFilingsUrl: "https://beian.miit.gov.cn/",
    internetFilings: isProdEnv ? internetFilingsXinDongWuLiu : internetFilings,
    internetFilingsUrl: "https://beian.mps.gov.cn/#/query/webSearch?code=42110002000194",
    poweredBy,
    themeColor: "#2143FC",
    loginLogo: ENV_HEADER_LOGO,
    loginBgColor: `${CDN_URL}/zhongsheng-customer/login-bg.jpg`,
    publicHeaderLogo: ENV_HEADER_LOGO,
    publicBanner: "",
    publicFooterLogo: isProdEnv ? `${CDN_URL}/logo/xindongwuliu-logo.png` : `${OSS_URL_SUPPLIER}/logo/lianyun-logo.svg`,
    accountAvatar: `${CDN_URL}/customer-admin/avatar.svg`,
    buttonTextColor: "#333"
};

/**
 * 主题
 */
class Theme {
    /**
     * 系统名称
     */
    private _systemName: string = "";
    private _systemSvg: string = "";
    /**
     * 系统简称
     */
    private _systemAbbreviation: string = "";
    /**
     * 系统英语名称
     */
    private _systemEnglishName: string = "";
    /**
     * 系统头部标语
     */
    private _systemHeaderSlogan: string = "";
    /**
     * 版权
     */
    private _copyright: string = "";

    /**
     * 备案
     */
    private _icpFilings: string = "";

    /**
     * 备案网址
     */
    private _icpFilingsUrl: string = "";
    /**
     * 联网备案信息
     */
    private _internetFilings: string = "";
    /**
     * 联网备案信息网址
     */
    private _internetFilingsUrl: string = "";
    /**
     * 创造者
     */
    private _poweredBy: string = "";

    /**
     * 主题颜色
     */
    private _themeColor: string = "";
    /**
     * 登录页logo
     */
    private _loginLogo: string = "";
    /**
     * 登录页背景图
     */
    private _loginBgColor: string = "";
    /**
     * 公众页头部logo
     */
    private _publicHeaderLogo: string = "";
    /**
     * 公众页横幅
     */
    private _publicBanner: string = "";
    /**
     * 公众页底部logo
     */
    private _publicFooterLogo: string = "";
    /**
     * 账号头像
     */
    private _accountAvatar: string = "";

    constructor() {}

    /**
     * 设置主题
     */
    setTheme(theme: ThemeOptions) {
        this._systemName = theme.systemName;
        this._systemSvg = theme.systemSvg;
        this._systemAbbreviation = theme.systemAbbreviation;
        this._systemEnglishName = theme.systemEnglishName;
        this._systemHeaderSlogan = theme.systemHeaderSlogan;
        this._copyright = theme.copyright;
        this._themeColor = theme.themeColor;
        this._loginLogo = theme.loginLogo;
        this._loginBgColor = theme.loginBgColor;
        this._publicHeaderLogo = theme.publicHeaderLogo;
        this._publicBanner = theme.publicBanner;
        this._publicFooterLogo = theme.publicFooterLogo;
        this._accountAvatar = theme.accountAvatar;
        this._icpFilings = theme.icpFilings;
        this._icpFilingsUrl = theme.icpFilingsUrl;
        this._internetFilings = theme.internetFilings;
        this._internetFilingsUrl = theme.internetFilingsUrl;
        this._poweredBy = theme.poweredBy;
    }

    get systemName() {
        return this._systemName;
    }

    get systemSvg() {
        return this._systemSvg;
    }

    get systemEnglishName() {
        return this._systemEnglishName;
    }

    get systemAbbreviation() {
        return this._systemAbbreviation;
    }

    get systemHeaderSlogan() {
        return this._systemHeaderSlogan;
    }

    get copyright() {
        return this._copyright;
    }

    get themeColor() {
        return this._themeColor;
    }

    get loginLogo() {
        return this._loginLogo;
    }

    get loginBgColor() {
        return this._loginBgColor;
    }

    get publicHeaderLogo() {
        return this._publicHeaderLogo;
    }

    get publicBanner() {
        return this._publicBanner;
    }

    get publicFooterLogo() {
        return this._publicFooterLogo;
    }

    get accountAvatar() {
        return this._accountAvatar;
    }

    get icpFilings() {
        return this._icpFilings;
    }

    get icpFilingsUrl() {
        return this._icpFilingsUrl;
    }

    get internetFilings() {
        return this._internetFilings;
    }

    get internetFilingsUrl() {
        return this._internetFilingsUrl;
    }

    get poweredBy() {
        return this._poweredBy;
    }

    /**
     * 单例
     * @private
     */
    private static instance: Theme;

    /**
     * 获取单例
     */
    static getInstance() {
        if (!Theme.instance) {
            Theme.instance = new Theme();
        }
        return Theme.instance;
    }
}

export const theme = Theme.getInstance();
